import { useEffect } from 'react';

function upsertMeta(attributeName, attributeValue, content) {
  if (!content) {
    return;
  }

  let element = document.head.querySelector(
    `meta[${attributeName}="${attributeValue}"]`
  );

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attributeName, attributeValue);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
}

function upsertCanonical(url) {
  if (!url) {
    return;
  }

  let element = document.head.querySelector('link[rel="canonical"]');

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }

  element.setAttribute('href', url);
}

function upsertStructuredData(payload) {
  const id = 'kyros-code-structured-data';
  let script = document.getElementById(id);

  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(payload);
}

function buildProfileUrl(profile) {
  if (profile?.siteUrl) {
    return profile.siteUrl.replace(/\/$/, '');
  }

  return window.location.origin;
}

export default function useSiteSeo({ portfolio, pathname = '/', noIndex = false, title }) {
  useEffect(() => {
    if (!portfolio) {
      return;
    }

    const profile = portfolio.profile;
    const canonicalRoot = buildProfileUrl(profile);
    const canonicalUrl = `${canonicalRoot}${pathname === '/' ? '' : pathname}`;
    const seoTitle =
      title ||
      profile.seo?.title ||
      `${profile.name} | ${profile.title} | ${profile.alias}`;
    const description =
      profile.seo?.description ||
      profile.summary ||
      `${profile.name} builds web, mobile, AI, IoT, and Web3 products.`;
    const keywords = profile.seo?.keywords?.join(', ') || '';
    const alternateNames = [...(profile.alternateNames || []), profile.alias].filter(Boolean);
    const sameAs = [
      profile.github,
      profile.socials?.linkedin,
      profile.socials?.youtube,
      profile.socials?.tiktok,
      profile.socials?.facebook,
    ].filter(Boolean);

    document.title = seoTitle;
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', keywords);
    upsertMeta('name', 'robots', noIndex ? 'noindex,nofollow' : 'index,follow');
    upsertMeta('property', 'og:title', seoTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('property', 'og:image', profile.seo?.ogImage || `${canonicalRoot}/og-image.jpg`);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', seoTitle);
    upsertMeta('name', 'twitter:description', description);
    upsertCanonical(canonicalUrl);
    upsertStructuredData({
      '@context': 'https://schema.org',
      '@type': 'Person',
        mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': canonicalUrl,
  },
      image: 'https://davisnwachukwu.com/images/me.jpg',
      name: profile.name,
      alternateName: alternateNames,
      url: canonicalRoot,
      email: profile.email ? `mailto:${profile.email}` : undefined,
      jobTitle: profile.title,
      description,
      sameAs,
      worksFor: {
        '@type': 'Organization',
        name: profile.brand,
      },
      knowsAbout: profile.seo?.keywords || [],
    });
  }, [noIndex, pathname, portfolio, title]);
}
